import { SortDirection, type Option } from "@intentsify/types";
import { DateTime } from "luxon";
import type { FetchDataParamsWithToken } from "types/FetchDataParams";
import type { UserAuditLogFilters } from "./UserAuditLog.filters";
import type {
  ListUserActivityLogsParams,
  UserAuditLogOrderBy,
} from "./UserAuditLog.requests";

type UserAuditLogUserOptionSource = {
  userId: number;
  displayName: string;
  email: string;
  companyId: number | null;
};

export type UserAuditLogDatePreset = "today" | "last7Days" | "last30Days";

export const defaultListUserActivityLogsParams: ListUserActivityLogsParams = {
  pageToken: "",
  order_by: "loggedAt",
  order: SortDirection.DESC,
  search: "",
  activityNames: [],
  startDate: "",
  endDate: "",
  companyId: null,
  userIds: [],
};

export const USER_AUDIT_LOG_DATE_PRESETS: Array<{
  label: string;
  value: UserAuditLogDatePreset;
}> = [
  { label: "Today", value: "today" },
  { label: "Last 7 days", value: "last7Days" },
  { label: "Last 30 days", value: "last30Days" },
];

export const getUserAuditLogDatePresetRange = (
  preset: UserAuditLogDatePreset,
  now = DateTime.utc(),
) => {
  const endDate = now.toUTC().toISODate() ?? "";

  const daysToSubtract: Record<UserAuditLogDatePreset, number> = {
    today: 0,
    last7Days: 6,
    last30Days: 29,
  };

  return {
    startDate:
      now.toUTC().minus({ days: daysToSubtract[preset] }).toISODate() ?? "",
    endDate,
  };
};

const getUserAuditLogUserOptionLabel = ({
  userId,
  displayName,
  email,
}: Pick<UserAuditLogUserOptionSource, "userId" | "displayName" | "email">) => {
  const name = displayName || email || `User ${userId}`;

  return email && email !== name ? `${name} (${email})` : name;
};

export const getUserAuditLogUserOptions = (
  users: UserAuditLogUserOptionSource[],
  companyId: number | null,
): Option<number>[] =>
  users
    .filter((user) => !companyId || user.companyId === companyId)
    .map((user) => ({
      value: user.userId,
      label: getUserAuditLogUserOptionLabel(user),
    }));

export const getUserAuditLogCompanyUserIds = ({
  users,
  companyId,
  userIds,
}: {
  users: UserAuditLogUserOptionSource[];
  companyId: number | null;
  userIds: number[];
}) => {
  if (!companyId || userIds.length === 0 || users.length === 0) {
    return userIds;
  }

  const companyUserIds = new Set(
    users
      .filter((user) => user.companyId === companyId)
      .map((user) => user.userId),
  );

  return userIds.filter((userId) => companyUserIds.has(userId));
};

export const getUserAuditLogFilterKey = (filters: UserAuditLogFilters) =>
  JSON.stringify({
    search: filters.search,
    activityNames: filters.activityNames,
    startDate: filters.startDate,
    endDate: filters.endDate,
    companyId: filters.companyId,
    userIds: filters.userIds,
  });

export const getListParams = (
  fetchDataParams: FetchDataParamsWithToken<UserAuditLogOrderBy>,
  filters: UserAuditLogFilters,
  previousFilters: UserAuditLogFilters,
): ListUserActivityLogsParams => {
  const didFiltersChange =
    getUserAuditLogFilterKey(filters) !==
    getUserAuditLogFilterKey(previousFilters);

  return {
    ...defaultListUserActivityLogsParams,
    ...fetchDataParams,
    pageToken: didFiltersChange ? "" : fetchDataParams.pageToken,
    search: filters.search,
    activityNames: filters.activityNames,
    startDate: filters.startDate,
    endDate: filters.endDate,
    companyId: filters.companyId,
    userIds: filters.userIds,
  };
};
